import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Loader2, Save, X, Upload, Trash2, Plus, Image as ImageIcon,
  Link2, Video, ChevronDown, ChevronUp
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  adsApi,
  type Ad,
  type AdMacro,
  type AdTracker,
  type NewAdTemplate,
} from '../../lib/api/ads'

// ─── Types ───

interface AdRowEditorProps {
  campaignId: number
  adUnitId: number
  adId: number | null
  onSave: () => void
  onCancel: () => void
  /**
   * Sizes available for this ad unit (from the parent's ad-unit fetch
   * — e.g. `["300x250", "728x90", "DesktopSkin"]`). Surfaced inside
   * the Trackers tab so users can scope a custom tracker to a single
   * size. Empty / undefined hides the dropdown's per-size options and
   * the field collapses to the "All Sizes" default.
   */
  adUnitSizes?: string[]
}

type EditorTab = 'basic' | 'advanced' | 'trackers'

interface FormState {
  title: string
  description: string
  destination_url: string
  action_text: string
  button_color: string
  image_url: string
  status: string
  content_type: string
  has_action: boolean
  has_description: boolean
  show_title: boolean
  image_only: boolean
  overlap_type: string
  default: boolean
  impression_tracker: string
  include_like_share: boolean
  short_url: string
  og_description: string
  label_title: string
  label_font_color: string
  label_background_color: string
  headline_fb: string
  publish_date: string
  publish_time: string
  unpublished_date: string
  unpublished_time: string
  video_5_tracker: string
  video_25_tracker: string
  video_50_tracker: string
  video_75_tracker: string
  video_100_tracker: string
}

/**
 * A single custom image slot returned by the ad API. The backend may
 * expose any number of these (`custom_image_2_url`, `custom_image_7_url`,
 * …) so we discover them at load time instead of hard-coding a range.
 *
 * `index`  – the N in `custom_image_N_url` (used to round-trip back to the
 *            same backend field on save).
 * `url`    – current url (existing remote image, or an object URL preview
 *            of a freshly-picked file before save).
 * `pendingFile` – the File the user just picked; non-null means we must
 *            send this slot as multipart on save.
 * `multiple` – the `custom_image_N_multiple` boolean toggle.
 */
interface CustomImageSlot {
  index: number
  url: string
  multiple: boolean
  pendingFile: File | null
  /**
   * Human-readable label for this slot's sub-tab, derived from the ad
   * API's `custom_image_macro_keys` map. Falls back to a generic
   * "Custom Image N" when the API doesn't provide a mapping for this
   * slot. We render whatever's here verbatim, so already-humanised
   * strings round-trip exactly.
   */
  label: string
}

/**
 * A single `custom_template_videos[]` entry surfaced inside the variant
 * attribute editor. Modelled to closely mirror {@link CustomImageSlot}
 * so the two sections share UX patterns (sub-tab per slot, file upload
 * widget, size cap, pending-file preview, etc.).
 *
 * `index` is the **1-based position** in the variant attribute's
 * `custom_template_videos` array at load time, and is the value we
 * round-trip as the `N` in `custom_video_N_*` keys on save. Newly
 * added slots aren't supported today (the BE seeds the array), so we
 * don't reuse this as a generation counter.
 *
 * `video_type === 'file'` is the only mode that participates in
 * multipart upload; every other type (youtube/vimeo/custom/…) takes a
 * pasted `link` URL instead, which is sent as a scalar field alongside
 * the other custom_video_N_* keys.
 */
interface CustomVideoSlot {
  index: number
  /** Backend id of the underlying `custom_template_videos` row, if any.
   *  Informational — not currently shipped back to the API. */
  backendId: number | null
  /** Either the existing remote `video_url` (or an object URL for a
   *  freshly-picked file before save). Mirrors {@link CustomImageSlot.url}. */
  url: string
  /** Non-file modes only: the pasted YouTube/Vimeo/custom embed link. */
  link: string
  /** One of {@link VARIANT_VIDEO_TYPE_OPTIONS} (typically `'file'`,
   *  `'youtube'`, or `'vimeo'`). Defaults to `'file'` for new entries;
   *  the BE may still surface legacy values (`'custom'`, `'uploaded'`)
   *  which we preserve via the dropdown's passthrough option. */
  video_type: string
  autoplay: boolean
  /** Non-null means we must send this slot as multipart on save. Only set
   *  for `video_type === 'file'`. */
  pendingFile: File | null
  /** Pre-derived sub-tab label (e.g. "Custom Video 1"). Symmetric with
   *  {@link CustomImageSlot.label}. When the variant attribute ships a
   *  `custom_video_macro_keys` map (e.g. `{ "1": "video_1" }`) we derive
   *  the label from the mapped key (stripped + humanised); otherwise we
   *  fall back to the generic `Custom Video N` label. */
  label: string
}

interface VariantFormState {
  /**
   * Backend id of the existing `variant_attribute` row, or `null` for
   * an ad that doesn't have one yet. When non-null we round-trip it as
   * `variant_attribute_attributes.id` so Rails' `accepts_nested_attributes_for`
   * updates the existing row instead of creating a duplicate.
   */
  id: number | null
  name: string
  images: CustomImageSlot[]
  videos: CustomVideoSlot[]
  /** Order-preserved list of `data.*` keys so the UI doesn't reshuffle
   *  on each keystroke. Keys are never removed by the editor. */
  dataKeys: string[]
  data: Record<string, string>
}

const INITIAL_FORM: FormState = {
  title: '',
  description: '',
  destination_url: '',
  action_text: '',
  button_color: '#000000',
  image_url: '',
  status: 'draft',
  content_type: 'standard_template',
  has_action: false,
  has_description: true,
  show_title: true,
  image_only: false,
  overlap_type: 'gradient',
  default: false,
  impression_tracker: '',
  include_like_share: false,
  short_url: '',
  og_description: '',
  label_title: '',
  label_font_color: '#000000',
  label_background_color: '#ffffff',
  headline_fb: '',
  publish_date: '',
  publish_time: '',
  unpublished_date: '',
  unpublished_time: '',
  video_5_tracker: '',
  video_25_tracker: '',
  video_50_tracker: '',
  video_75_tracker: '',
  video_100_tracker: '',
}

const INITIAL_VARIANT: VariantFormState = {
  id: null,
  name: '',
  images: [],
  videos: [],
  dataKeys: [],
  data: {},
}

// ── Data placeholder field typing ──
// The backend's `data.*` keys carry a tiny convention: a `_txt` suffix or
// the word "txt" in the key means "free-form text", while `_num` / "num"
// (plus a few semantic hints like time/age/duration/position/direction)
// means "numeric". Anything else falls back to a single-line text input.
// Long-form text (descriptions, T&Cs, body copy) gets a textarea instead.
type VariantDataFieldKind = 'text' | 'longtext' | 'number' | 'color'

const NUMBER_KEY_HINTS = ['time', 'age', 'duration', 'count', 'qty', 'quantity', 'amount', 'price', 'percentage', 'percent', 'rate', 'year', 'month', 'day', 'hour', 'minute', 'second']
const LONG_TEXT_HINTS = ['description', 'body', 'long', 'tnc', 'footer', 'sub_title', 'subtitle', 'paragraph', 'disclaimer']

function classifyVariantDataField(key: string, value: string): VariantDataFieldKind {
  const k = key.toLowerCase()
  // Explicit suffix/substring tags win first so backend authors have a
  // reliable opt-out from the heuristic.
  // `_color` (or "color" anywhere in the key, e.g. `cta_color`,
  // `background_color`) gets a colour picker. Checked before `_num` so
  // hex values like `#ff8800` aren't mis-classified as numbers.
  if (/(^|_)color($|_)/.test(k) || k.includes('color')) return 'color'
  if (/(^|_)num($|_)/.test(k) || NUMBER_KEY_HINTS.some(h => k.includes(h))) return 'number'
  if (/(^|_)txt($|_)/.test(k)) {
    // _txt fields default to text but can still be promoted to a textarea
    // when the value or key shape clearly calls for it.
    if (LONG_TEXT_HINTS.some(h => k.includes(h)) || valueLooksLong(value)) return 'longtext'
    return 'text'
  }
  if (LONG_TEXT_HINTS.some(h => k.includes(h)) || valueLooksLong(value)) return 'longtext'
  return 'text'
}

/**
 * Coerce whatever the backend gave us (CSS colour names, partial hex,
 * `rgb(…)`) into a `#rrggbb` string the `<input type="color">` element
 * accepts. Returns the input untouched for the text twin so users still
 * see the raw value they typed. Empty values fall back to `#000000` for
 * the picker but stay empty in the text field.
 */
function toPickerHex(value: string): string {
  const v = (value || '').trim()
  if (!v) return '#000000'
  // Short hex (#abc) → expand to #aabbcc.
  const short = v.match(/^#?([0-9a-fA-F]{3})$/)
  if (short) {
    const [r, g, b] = short[1]
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase()
  }
  const long = v.match(/^#?([0-9a-fA-F]{6})$/)
  if (long) return `#${long[1].toLowerCase()}`
  return '#000000'
}

function valueLooksLong(value: string): boolean {
  return Boolean(value) && (value.includes('\n') || value.length > 60)
}

// Discover every `custom_image_N_url` field on a variant attribute and
// return them sorted ascending by N. Pairs each url with its matching
// `custom_image_N_multiple` boolean (defaulting to false when missing)
// and resolves a human-readable `label` from the ad's
// `custom_image_macro_keys` map when one is provided.
//
// The macro map looks like:
//   { "1": "custom_image_1_product_image_1", "2": "custom_image_2_…" }
// We strip the leading `custom_image_<N>_` prefix from each value and
// humanise the remainder, so slot 1 becomes "Product Image 1". Slots
// without a mapping fall back to the generic "Custom Image N" label.
function deriveSlotLabel(index: number, macroKeys: Record<string | number, string> | null | undefined): string {
  const raw = macroKeys?.[index] ?? macroKeys?.[String(index)]
  if (raw && typeof raw === 'string') {
    // Match `custom_image_<index>_<rest>` and humanise <rest>. We anchor
    // on the slot's index so a stray off-by-one mapping doesn't quietly
    // mislabel a sibling slot.
    const stripPrefix = new RegExp(`^custom_image_${index}_`)
    const remainder = raw.replace(stripPrefix, '').trim()
    if (remainder) return humanise(remainder)
    // Macro key was literally just `custom_image_<n>` — still humanise so
    // we get a reasonable "Custom Image N" label.
    return humanise(raw)
  }
  return `Custom Image ${index}`
}

function discoverImageSlots(
  attr: Record<string, any>,
  macroKeys: Record<string | number, string> | null | undefined,
): CustomImageSlot[] {
  const re = /^custom_image_(\d+)_url$/
  const slots: CustomImageSlot[] = []
  for (const key of Object.keys(attr)) {
    const m = key.match(re)
    if (!m) continue
    const index = Number(m[1])
    slots.push({
      index,
      url: attr[key] || '',
      multiple: Boolean(attr[`custom_image_${index}_multiple`]),
      pendingFile: null,
      label: deriveSlotLabel(index, macroKeys),
    })
  }
  return slots.sort((a, b) => a.index - b.index)
}

// Resolve the sub-tab label for a video slot from the variant
// attribute's `custom_video_macro_keys` map. Shape mirrors
// `custom_image_macro_keys` on the image side:
//   { "1": "video_1", "2": "hero_video" }
// The mapped value is treated as a template macro name — we strip a
// leading `video_<index>_` (or bare `video_`) prefix and humanise the
// remainder so `video_1` → "Video 1" and `custom_video_2_hero` → "Hero".
// Slots without a mapping fall back to the generic "Custom Video N".
function deriveVideoSlotLabel(
  index: number,
  macroKeys: Record<string | number, string> | null | undefined,
): string {
  const raw = macroKeys?.[index] ?? macroKeys?.[String(index)]
  if (raw && typeof raw === 'string') {
    // Try the most specific prefix first (`custom_video_<n>_…` or
    // `video_<n>_…`), then fall back to a bare `video_` strip, then
    // humanise whatever's left.
    const stripped = raw
      .replace(new RegExp(`^custom_video_${index}_`), '')
      .replace(new RegExp(`^video_${index}(_|$)`), '')
      .replace(/^custom_video_/, '')
      .replace(/^video_/, '')
      .trim()
    if (stripped) return humanise(stripped)
    return humanise(raw)
  }
  return `Custom Video ${index}`
}

// Turn the variant attribute's `custom_template_videos` array (richer
// shape than the flat image slots — see {@link AdVariantAttribute} in
// `lib/api/ads.ts`) into the editor's {@link CustomVideoSlot[]} state.
// Position-based 1-indexing matches the image side and gives us stable
// `custom_video_N_*` keys for the update payload.
//
// When the variant attribute also ships a `custom_video_macro_keys`
// map, we ensure a slot exists for **every** macro-declared index even
// if `custom_template_videos[]` is empty or shorter than the macro
// list. This surfaces upload tabs for freshly-templated variants
// where the BE hasn't seeded the videos array yet — the user can then
// drop in files and the save payload will create the missing rows
// (they're emitted without an `id`, which Rails' accepts_nested_
// attributes_for treats as new records).
function discoverVideoSlots(
  attr: Record<string, any>,
  macroKeys: Record<string | number, string> | null | undefined,
): CustomVideoSlot[] {
  const raw = Array.isArray(attr?.custom_template_videos) ? attr.custom_template_videos : []
  const slots: CustomVideoSlot[] = raw.map((entry: any, i: number) => {
    const index = i + 1
    const videoType = typeof entry?.video_type === 'string' && entry.video_type
      ? entry.video_type
      : 'file'
    return {
      index,
      backendId: typeof entry?.id === 'number' ? entry.id : null,
      url: entry?.video_url || '',
      link: entry?.link || '',
      video_type: videoType,
      autoplay: entry?.autoplay ?? true,
      pendingFile: null,
      label: deriveVideoSlotLabel(index, macroKeys),
    }
  })

  // Pad with empty placeholder slots for any macro-declared indices
  // that don't already have a matching `custom_template_videos[]`
  // entry, so the sub-tabs show up as upload targets.
  if (macroKeys && typeof macroKeys === 'object') {
    const macroIndices = Object.keys(macroKeys)
      .map(k => Number(k))
      .filter(n => Number.isFinite(n) && n > 0)
    const existing = new Set(slots.map(s => s.index))
    for (const idx of macroIndices) {
      if (existing.has(idx)) continue
      slots.push({
        index: idx,
        backendId: null,
        url: '',
        link: '',
        video_type: 'file',
        autoplay: true,
        pendingFile: null,
        label: deriveVideoSlotLabel(idx, macroKeys),
      })
    }
  }

  return slots.sort((a, b) => a.index - b.index)
}

// Humanise a `data.*` key like `benefit_cash_txt` ⟶ "Benefit Cash".
// We strip the trailing `_txt` / `_num` convention suffix (it's a
// backend-side type hint, not part of the user-facing label) and any
// leading/trailing/duplicate `txt`/`num` segments, then title-case the
// remaining underscore-separated words.
function humanise(key: string): string {
  return key
    .replace(/(^|_)(txt|num)(_|$)/gi, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim()
}

// Only three statuses are user-selectable in the variant editor. The API
// historically exposed more (`draft`, `paused`, `archived`) but they are
// no longer surfaced to creative users — we keep the value through if the
// backend sends one of the legacy statuses (see the rendering logic), we
// just don't offer them as options to pick from.
const STATUS_OPTIONS = [
  'published',
  'ready_to_publish',
  'unpublished',
]

const STATUS_LABELS: Record<string, string> = {
  published: 'Published',
  ready_to_publish: 'Ready to publish',
  unpublished: 'Unpublished',
}

// Variant image upload size cap. Kept as a small constant pair so the
// number is easy to surface in the UI hint + the toast error message.
const MAX_VARIANT_IMAGE_KB = 500
const MAX_VARIANT_IMAGE_BYTES = MAX_VARIANT_IMAGE_KB * 1024

// Variant video upload size cap. Larger than the image cap because
// even short H.264 clips dwarf a 500 KB image; 4 MB lets a ~10s
// 1080p clip through (the screenshot reference had bitrate ~629 kbps).
// Surfaced in the upload hint + the toast on overflow.
const MAX_VARIANT_VIDEO_MB = 4
const MAX_VARIANT_VIDEO_BYTES = MAX_VARIANT_VIDEO_MB * 1024 * 1024

const OVERLAP_OPTIONS = ['gradient', 'solid', 'none']

// Video type options surfaced inside the variant attribute's Custom
// Videos slot editor. The classic per-ad Video section (with its own
// link / title / category / etc.) used to live at the bottom of the
// Basic tab and had a wider vocabulary (`custom`, `uploaded`), but was
// removed because it duplicated the Custom Videos UX driven by
// `custom_template_videos[]`. We only expose the three types that
// actually make sense per variant. `uploaded` is the BE-side persisted
// twin of `file` (same storage, different post-save name) so we don't
// need a separate option; `custom` was a legacy embed we no longer
// support. `file` is a first-class option (labelled "Upload File") so
// users can switch back to uploading after picking an external host.
const VARIANT_VIDEO_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'file', label: 'Upload File' },
  { value: 'youtube', label: 'Youtube' },
  { value: 'vimeo', label: 'Vimeo' },
]
// Macro condition vocabulary supported by the backend. Stored as the
// machine value (`equal`, `not_equal`, …) and rendered with the
// user-facing label below. Order mirrors the Income reference UI's
// dropdown so users coming from there see a familiar layout.
const MACRO_CONDITION_OPTIONS: { value: string; label: string }[] = [
  { value: 'equal', label: 'Equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' },
  { value: 'greater_than_or_equal', label: 'Greater than or equal' },
  { value: 'less_than_or_equal', label: 'Less than or equal' },
  { value: 'not_equal', label: 'Not equal' },
  { value: 'starts_with', label: 'Start with' },
  { value: 'not_starts_with', label: 'Not start with' },
  { value: 'exist', label: 'Exist' },
  { value: 'not_exist', label: 'Not exist' },
  { value: 'between', label: 'Between' },
  { value: 'not_contains', label: 'Not contains' },
]

// ─── Component ───

export function AdRowEditor({
  campaignId,
  adUnitId,
  adId,
  onSave,
  onCancel,
  adUnitSizes = [],
}: AdRowEditorProps) {
  const [activeTab, setActiveTab] = useState<EditorTab>('basic')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [form, setForm] = useState<FormState>(INITIAL_FORM)
  const [variant, setVariant] = useState<VariantFormState>(INITIAL_VARIANT)
  const [macros, setMacros] = useState<Omit<AdMacro, 'id' | 'ad_id' | 'ad_unit_id' | 'smart_tag_id'>[]>([])
  const [trackers, setTrackers] = useState<Omit<AdTracker, 'id' | 'ad_id' | 'ad_unit_id' | 'created_at' | 'updated_at'>[]>([])
  const [showVariantSection, setShowVariantSection] = useState(false)
  // Index into `variant.images` for the active Image sub-tab.
  const [activeImageSlot, setActiveImageSlot] = useState(0)
  // Index into `variant.videos` for the active Video sub-tab. Mirrors
  // `activeImageSlot` so both sections behave the same way.
  const [activeVideoSlot, setActiveVideoSlot] = useState(0)
  const imageInputRef = useRef<HTMLInputElement>(null!)
  // Destination URL is required for an ad to actually be clickable. We
  // gate the Save button on it and surface inline + scroll-on-submit
  // feedback (matching the Start-with-AI modal's pattern). `touched`
  // flips on blur or on a failed save attempt so we don't yell at the
  // user while they're still typing.
  const [destinationUrlTouched, setDestinationUrlTouched] = useState(false)
  const destinationUrlRef = useRef<HTMLInputElement>(null)
  const trimmedDestinationUrl = form.destination_url.trim()
  const isDestinationUrlValid = (() => {
    if (!trimmedDestinationUrl) return false
    try {
      const u = new URL(trimmedDestinationUrl)
      return u.protocol === 'http:' || u.protocol === 'https:'
    } catch {
      return false
    }
  })()
  const showDestinationUrlError = destinationUrlTouched && !isDestinationUrlValid

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    // Two hydration paths sharing the same populateForm pipeline:
    //   • Editing an existing ad → `GET /ads/:id` → full Ad.
    //   • Creating a new one (adId == null) → `GET /ads/new` → a
    //     not-yet-persisted template that already carries the macro-keys
    //     maps, default variant_attribute.data.* placeholders, tracker
    //     scaffolding, etc. — everything the editor needs to render the
    //     same tabs a user would see when editing an existing row.
    //
    // Using the same populateForm for both keeps the "Add variant" UI
    // pixel-identical to the "Edit variant" UI; we just start from the
    // BE-supplied blank template instead of a persisted row.
    const request = adId
      ? adsApi.getAd(campaignId, adUnitId, adId)
      : adsApi.getNewAd(campaignId, adUnitId)
    request
      .then(ad => {
        if (cancelled) return
        populateForm(ad)
      })
      .catch(() => {
        if (!cancelled) toast.error(adId ? 'Failed to load ad details' : 'Failed to load new variant form')
      })
      .finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
  }, [adId, campaignId, adUnitId])

  // Accepts either a fully-hydrated Ad (edit flow) or a NewAdTemplate
  // (create flow via `GET /ads/new`). Both shapes route through the
  // same optional-chaining / `|| default` reads below so a partial
  // template with mostly-null fields lands on the INITIAL_FORM defaults
  // without any special-casing.
  function populateForm(ad: Ad | NewAdTemplate) {
    setForm({
      title: ad.title || '',
      description: ad.description || '',
      destination_url: ad.destination_url || '',
      action_text: ad.action_text || '',
      button_color: ad.button_color || '#000000',
      image_url: ad.image_url || '',
      status: ad.status || 'draft',
      content_type: ad.content_type || 'standard_template',
      has_action: ad.has_action ?? false,
      has_description: ad.has_description ?? true,
      show_title: ad.show_title ?? true,
      image_only: ad.image_only ?? false,
      overlap_type: ad.overlap_type || 'gradient',
      default: ad.default ?? false,
      impression_tracker: ad.impression_tracker || '',
      include_like_share: ad.include_like_share ?? false,
      short_url: ad.short_url || '',
      og_description: ad.og_description || '',
      label_title: ad.label_title || '',
      label_font_color: ad.label_font_color || '#000000',
      label_background_color: ad.label_background_color || '#ffffff',
      headline_fb: ad.headline_fb || '',
      publish_date: ad.publish_date || '',
      publish_time: ad.publish_time || '',
      unpublished_date: ad.unpublished_date || '',
      unpublished_time: ad.unpublished_time || '',
      video_5_tracker: ad.video_5_tracker || '',
      video_25_tracker: ad.video_25_tracker || '',
      video_50_tracker: ad.video_50_tracker || '',
      video_75_tracker: ad.video_75_tracker || '',
      video_100_tracker: ad.video_100_tracker || '',
    })

    // The classic `ad.video` block (single per-ad video, distinct from
    // the variant attribute's `custom_template_videos[]`) used to hydrate
    // a dedicated Video collapsible section here. That UI has been
    // removed — videos are edited exclusively through Custom Videos on
    // the variant attribute — so we no longer read `ad.video`.

    if (ad.variant_attribute) {
      setShowVariantSection(true)
      const attr = ad.variant_attribute as Record<string, any>
      // `custom_image_macro_keys` can live either on the ad payload
      // (current backend) or — defensively — on the variant attribute
      // itself if the API ever moves it. We accept both locations so
      // this code keeps working across that potential migration.
      const macroKeys =
        (ad as any).custom_image_macro_keys ??
        attr.custom_image_macro_keys ??
        null
      // Videos got their own macro keys map later than images. The BE
      // currently ships it on `variant_attribute.custom_video_macro_keys`
      // (see the reference response with `{ "1": "video_1" }`); we also
      // accept an ad-level fallback for symmetry with `custom_image_macro_keys`
      // in case it migrates later.
      const videoMacroKeys =
        (ad as any).custom_video_macro_keys ??
        attr.custom_video_macro_keys ??
        null
      const images = discoverImageSlots(attr, macroKeys)
      const videos = discoverVideoSlots(attr, videoMacroKeys)
      const rawData = (attr.data || {}) as Record<string, any>
      // Coerce every data value to a string so the inputs stay controlled
      // even if the backend sends numbers / nulls. Order from the server
      // is preserved via Object.keys (most engines iterate insertion order
      // for string keys, which matches how Rails serialises hashes).
      const dataKeys = Object.keys(rawData)
      const data: Record<string, string> = {}
      for (const k of dataKeys) data[k] = rawData[k] == null ? '' : String(rawData[k])
      setVariant({
        id: typeof attr.id === 'number' ? attr.id : null,
        name: attr.name || '',
        images,
        videos,
        dataKeys,
        data,
      })
      setActiveImageSlot(0)
      setActiveVideoSlot(0)
    }

    if (ad.macros?.length) {
      setMacros(ad.macros.map(m => ({ name: m.name, condition: m.condition, value: m.value })))
    }

    if (ad.trackers?.length) {
      setTrackers(ad.trackers.map(t => ({
        name: t.name,
        impression_url: t.impression_url,
        click_url: t.click_url,
        ad_unit_size: t.ad_unit_size,
        macro_value: t.macro_value,
      })))
    }
  }

  const updateForm = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }, [])

  const updateVariant = useCallback(<K extends keyof VariantFormState>(key: K, value: VariantFormState[K]) => {
    setVariant(prev => ({ ...prev, [key]: value }))
  }, [])

  const updateImageSlot = useCallback((slotIdx: number, patch: Partial<CustomImageSlot>) => {
    setVariant(prev => ({
      ...prev,
      images: prev.images.map((s, i) => (i === slotIdx ? { ...s, ...patch } : s)),
    }))
  }, [])

  const updateVideoSlot = useCallback((slotIdx: number, patch: Partial<CustomVideoSlot>) => {
    setVariant(prev => ({
      ...prev,
      videos: prev.videos.map((s, i) => (i === slotIdx ? { ...s, ...patch } : s)),
    }))
  }, [])

  const updateVariantDataValue = useCallback((key: string, value: string) => {
    setVariant(prev => ({ ...prev, data: { ...prev.data, [key]: value } }))
  }, [])

  const handleSave = async () => {
    // Destination URL is mandatory — bail before we even start the
    // multipart upload spinner if it's missing/invalid. We mark it
    // touched (which paints the red border + inline error), bounce the
    // editor back to the Basic tab if the user is on Advanced/Trackers,
    // and scroll/focus the field so it's immediately actionable.
    if (!isDestinationUrlValid) {
      setDestinationUrlTouched(true)
      setActiveTab('basic')
      // Defer until after the tab switch renders so the input is
      // actually mounted before we try to scroll/focus it.
      requestAnimationFrame(() => {
        destinationUrlRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        destinationUrlRef.current?.focus({ preventScroll: true })
      })
      toast.error('Destination URL is required')
      return
    }
    setIsSaving(true)
    try {
      const payload: Record<string, any> = {}

      if (form.title) payload.title = form.title
      if (form.description) payload.description = form.description
      if (form.destination_url) payload.destination_url = form.destination_url
      if (form.action_text) payload.action_text = form.action_text
      if (form.button_color && form.button_color !== '#000000') payload.button_color = form.button_color
      if (form.image_url) payload.image_url = form.image_url
      payload.status = form.status
      if (form.content_type) payload.content_type = form.content_type
      payload.has_action = form.has_action
      payload.has_description = form.has_description
      payload.show_title = form.show_title
      payload.image_only = form.image_only
      if (form.overlap_type) payload.overlap_type = form.overlap_type
      payload.default = form.default

      if (form.impression_tracker) payload.impression_tracker = form.impression_tracker
      payload.include_like_share = form.include_like_share
      if (form.short_url) payload.short_url = form.short_url
      if (form.og_description) payload.og_description = form.og_description
      if (form.label_title) payload.label_title = form.label_title
      if (form.label_font_color && form.label_font_color !== '#000000') payload.label_font_color = form.label_font_color
      if (form.label_background_color && form.label_background_color !== '#ffffff') payload.label_background_color = form.label_background_color
      if (form.headline_fb) payload.headline_fb = form.headline_fb
      if (form.publish_date) payload.publish_date = form.publish_date
      if (form.publish_time) payload.publish_time = form.publish_time
      if (form.unpublished_date) payload.unpublished_date = form.unpublished_date
      if (form.unpublished_time) payload.unpublished_time = form.unpublished_time

      if (form.video_5_tracker) payload.video_5_tracker = form.video_5_tracker
      if (form.video_25_tracker) payload.video_25_tracker = form.video_25_tracker
      if (form.video_50_tracker) payload.video_50_tracker = form.video_50_tracker
      if (form.video_75_tracker) payload.video_75_tracker = form.video_75_tracker
      if (form.video_100_tracker) payload.video_100_tracker = form.video_100_tracker

      // NB: the classic single-per-ad `video_attributes` payload was
      // emitted here when the (now-removed) Video collapsible section
      // was open with a link. Videos are exclusively edited via the
      // variant attribute's `custom_template_videos_attributes` array
      // now (see the block further down), so we no longer ship
      // `ad[video_attributes]`.

      // ── Variant attribute ──
      // Send *every* slot/key we loaded so the backend doesn't drop
      // placeholders the user didn't touch. Files that the user picked are
      // hoisted into a separate `variantFiles` map keyed by their
      // fully-qualified FormData name; if any are present we switch to a
      // multipart request below.
      const variantFiles: Record<string, File> = {}
      if (
        showVariantSection &&
        (variant.name || variant.images.length > 0 || variant.videos.length > 0 || variant.dataKeys.length > 0)
      ) {
        const variantPayload: Record<string, any> = {}
        // Round-trip the existing variant_attribute id so Rails'
        // accepts_nested_attributes_for updates the same row instead of
        // creating a new one. Omit the key entirely when there's no
        // existing variant_attribute (new-ad flow).
        if (variant.id != null) variantPayload.id = variant.id
        if (variant.name) variantPayload.name = variant.name

        // The backend's `custom_image_N_url` setter only accepts a fresh
        // upload — passing the existing remote URL back fails validation.
        // So we always emit the slot's keys (so the payload shape stays
        // stable and the `_multiple` toggle still round-trips), but we
        // leave `_url` as an empty string unless the user just picked a
        // new file. Picked files travel out-of-band as multipart parts
        // under `ad[variant_attribute_attributes][custom_image_N]`; the
        // BE wires them up to the slot and ignores our empty `_url`.
        for (const slot of variant.images) {
          if (slot.pendingFile) {
            variantFiles[`ad[variant_attribute_attributes][custom_image_${slot.index}]`] = slot.pendingFile
          }
          variantPayload[`custom_image_${slot.index}_url`] = ''
          variantPayload[`custom_image_${slot.index}_multiple`] = slot.multiple
        }

        // Custom-template videos use Rails' nested-attributes shape:
        //   ad[variant_attribute_attributes][custom_template_videos_attributes][N][id]         (existing rows)
        //   ad[variant_attribute_attributes][custom_template_videos_attributes][N][video]      (file)
        //   ad[variant_attribute_attributes][custom_template_videos_attributes][N][video_type]
        //   ad[variant_attribute_attributes][custom_template_videos_attributes][N][autoplay]
        //   ad[variant_attribute_attributes][custom_template_videos_attributes][N][link]       (non-file)
        //
        // We emit *every* discovered video slot on every save (matching
        // how custom images work): the payload shape stays stable and
        // the BE always sees the full array. Slots the user didn't
        // touch still round-trip their `id` + `video_type` + `autoplay`
        // (+ `link` if applicable), which lets Rails treat them as
        // "unchanged" via accepts_nested_attributes_for. Only the
        // `[video]` file part is conditional — we upload the file
        // out-of-band only for slots where the user picked a new one.
        //
        // Positional `N` follows the discovery order (i.e. the order
        // the BE returned the array), so `id`s line up 1-to-1 with the
        // original response. Slots without a `backendId` (new rows,
        // not currently reachable from the UI) are shipped without
        // the `[id]` key.
        const videoEntries: Array<Record<string, any>> = []
        const videoFilesByIndex: Array<File | null> = []
        for (const slot of variant.videos) {
          const isFile = slot.video_type === 'file'
          const hasNewFile = isFile && !!slot.pendingFile
          const entry: Record<string, any> = {
            video_type: slot.video_type,
            autoplay: slot.autoplay,
          }
          if (slot.backendId != null) entry.id = slot.backendId
          // For non-file modes always send the link (empty string
          // clears any previously stored value, mirroring how image
          // slots emit an empty `_url`). File mode omits `link`
          // because the BE derives the URL from the uploaded blob.
          if (!isFile) entry.link = slot.link || ''
          videoEntries.push(entry)
          videoFilesByIndex.push(hasNewFile ? slot.pendingFile : null)
        }
        if (videoEntries.length > 0) {
          // `appendNested` (see lib/api/ads.ts) serialises arrays of
          // objects using numeric indices (`[0]`, `[1]`, …) precisely
          // for this Rails accepts_nested_attributes_for shape — the
          // file part below also uses `[N]` explicitly, and the two
          // sides MUST line up or Rails parses the file and the
          // metadata as separate array entries (silently detaching the
          // upload from its `video_type` / `autoplay`).
          variantPayload.custom_template_videos_attributes = videoEntries
          videoFilesByIndex.forEach((file, i) => {
            if (!file) return
            variantFiles[`ad[variant_attribute_attributes][custom_template_videos_attributes][${i}][video]`] = file
          })
        }

        if (variant.dataKeys.length > 0) {
          const data: Record<string, string> = {}
          for (const k of variant.dataKeys) data[k] = variant.data[k] ?? ''
          variantPayload.data = data
        }

        payload.variant_attribute_attributes = variantPayload
      }

      if (macros.length > 0) {
        payload.macros_attributes = macros
          .filter(m => m.name)
          .map(m => ({ name: m.name, condition: m.condition, value: m.value }))
      }

      if (trackers.length > 0) {
        payload.trackers_attributes = trackers
          .filter(t => t.name || t.impression_url || t.click_url)
          .map(t => ({
            name: t.name,
            impression_url: t.impression_url || undefined,
            click_url: t.click_url || undefined,
            ad_unit_size: t.ad_unit_size || undefined,
            macro_value: t.macro_value || undefined,
          }))
      }

      const hasFiles = Object.keys(variantFiles).length > 0
      if (adId) {
        if (hasFiles) {
          await adsApi.updateAdMultipart(campaignId, adUnitId, adId, payload, variantFiles)
        } else {
          await adsApi.updateAd(campaignId, adUnitId, adId, payload)
        }
        toast.success('Variant updated')
      } else {
        // Create mirrors update: multipart when there are pending file
        // uploads (custom images / custom_template_videos_attributes
        // [N][video]), plain JSON otherwise. Same payload shape either
        // way — `createAdMultipart` just flattens `data` into `ad[...]`
        // keys and appends the pre-namespaced file parts alongside.
        if (hasFiles) {
          await adsApi.createAdMultipart(campaignId, adUnitId, payload, variantFiles)
        } else {
          await adsApi.createAd(campaignId, adUnitId, payload)
        }
        toast.success('Variant created')
      }
      onSave()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save variant')
    } finally {
      setIsSaving(false)
    }
  }

  // Macros are seeded from the ad API and are never added/removed in the
  // UI — only condition + value are editable. We keep `updateMacro` to
  // mutate those two fields in place.
  const updateMacro = (idx: number, field: 'condition' | 'value', value: string) => {
    setMacros(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m))
  }

  const addTracker = () => {
    setTrackers(prev => [...prev, { name: '', impression_url: null, click_url: null, ad_unit_size: null, macro_value: null }])
  }

  const updateTracker = (idx: number, field: string, value: string | null) => {
    setTrackers(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t))
  }

  const removeTracker = (idx: number) => {
    setTrackers(prev => prev.filter((_, i) => i !== idx))
  }

  // Object-URL cleanup: when a slot's pendingFile changes we create an
  // object URL for its preview. Revoke previous URLs when the form state
  // is replaced (e.g. switching ads) so we don't leak blobs.
  useEffect(() => {
    return () => {
      variant.images.forEach(s => {
        if (s.pendingFile && s.url.startsWith('blob:')) URL.revokeObjectURL(s.url)
      })
      variant.videos.forEach(s => {
        if (s.pendingFile && s.url.startsWith('blob:')) URL.revokeObjectURL(s.url)
      })
    }
    // We deliberately leave `variant.images`/`variant.videos` out of the
    // dep array: the effect only needs to run on unmount. The slot-level
    // handlers revoke the *previous* preview synchronously when a new
    // file is picked.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleVariantSlotFile = useCallback((slotIdx: number, file: File) => {
    // Hard cap variant images at 500 KB. The static template renderer
    // inlines these into the ad markup, so anything bigger blows up the
    // payload size + slows the preview iframe noticeably. We reject at
    // the picker level so the user gets feedback before they hit Save
    // (saving is async + multipart and the failure mode is much worse).
    if (file.size > MAX_VARIANT_IMAGE_BYTES) {
      toast.error(
        `Image is ${(file.size / 1024).toFixed(0)} KB. Variant images must be ${MAX_VARIANT_IMAGE_KB} KB or less.`
      )
      return
    }
    setVariant(prev => {
      const next = prev.images.map((s, i) => {
        if (i !== slotIdx) return s
        if (s.pendingFile && s.url.startsWith('blob:')) URL.revokeObjectURL(s.url)
        return { ...s, pendingFile: file, url: URL.createObjectURL(file) }
      })
      return { ...prev, images: next }
    })
  }, [])

  // Mirrors `handleVariantSlotFile` for videos: validate size cap,
  // revoke any prior blob preview, switch the slot into `file` mode
  // (uploading a file always implies `video_type === 'file'` —
  // otherwise we'd send a file *and* a YouTube link, which the BE
  // would have no sensible way to reconcile).
  const handleVariantVideoFile = useCallback((slotIdx: number, file: File) => {
    if (file.size > MAX_VARIANT_VIDEO_BYTES) {
      toast.error(
        `Video is ${(file.size / 1024 / 1024).toFixed(1)} MB. Variant videos must be ${MAX_VARIANT_VIDEO_MB} MB or less.`
      )
      return
    }
    setVariant(prev => {
      const next = prev.videos.map((s, i) => {
        if (i !== slotIdx) return s
        if (s.pendingFile && s.url.startsWith('blob:')) URL.revokeObjectURL(s.url)
        return {
          ...s,
          pendingFile: file,
          url: URL.createObjectURL(file),
          video_type: 'file',
          link: '',
        }
      })
      return { ...prev, videos: next }
    })
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 text-primary-600 animate-spin mr-2" />
        <span className="text-xs text-gray-500 dark:text-gray-400">Loading variant details...</span>
      </div>
    )
  }

  const tabs: { key: EditorTab; label: string }[] = [
    { key: 'basic', label: 'Basic' },
    { key: 'advanced', label: 'Advanced' },
    { key: 'trackers', label: 'Trackers' },
  ]

  return (
    <div className="border-t border-gray-200 dark:border-gray-800">
      {/* Tab navigation */}
      <div className="flex items-center justify-between px-4 pt-3 pb-0">
        <div className="flex items-center gap-1">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 rounded-t-lg text-xs font-medium transition-colors border border-b-0 ${
                activeTab === tab.key
                  ? 'bg-white dark:bg-gray-950 border-gray-200 dark:border-gray-700 text-primary-700 dark:text-primary-400'
                  : 'bg-transparent border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <span className="text-[10px] text-gray-400">
          {adId ? `Editing #${adId}` : 'New Variant'}
        </span>
      </div>

      {/* Tab content */}
      <div className="bg-white dark:bg-gray-950 border-t border-gray-200 dark:border-gray-700 px-4 py-4 max-h-[420px] overflow-y-auto">
        {activeTab === 'basic' && renderBasicTab()}
        {activeTab === 'advanced' && renderAdvancedTab()}
        {activeTab === 'trackers' && renderTrackersTab()}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/60">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
        >
          {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          {adId ? 'Update' : 'Create'}
        </button>
      </div>
    </div>
  )

  // Status <select> shared by both layouts. We always render the active
  // value as an option (even if it's a legacy status not in the new
  // 3-option set) so the dropdown reflects what's actually stored —
  // users can still change it to one of the three allowed values.
  function renderStatusSelect() {
    const includesActive = STATUS_OPTIONS.includes(form.status)
    return (
      <select
        value={form.status}
        onChange={e => updateForm('status', e.target.value)}
        className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
      >
        {!includesActive && form.status && (
          <option value={form.status}>
            {STATUS_LABELS[form.status] ?? form.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </option>
        )}
        {STATUS_OPTIONS.map(s => (
          <option key={s} value={s}>{STATUS_LABELS[s] ?? s}</option>
        ))}
      </select>
    )
  }

  // Destination URL is rendered in two layouts (classic + variant-backed)
  // so we factor it into a single helper. It owns its own red-asterisk
  // label, validation styling, inline error message and an ARIA hint.
  function renderDestinationUrlField() {
    const baseCls = 'w-full pl-8 pr-3 py-2 text-xs rounded-lg border bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1'
    const stateCls = showDestinationUrlError
      ? 'border-red-400 dark:border-red-500/70 focus:ring-red-500/40'
      : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
    return (
      <div>
        <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-400 mb-1">
          Destination URL <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <div className="relative">
          <Link2 className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            ref={destinationUrlRef}
            type="url"
            required
            aria-required="true"
            aria-invalid={showDestinationUrlError}
            value={form.destination_url}
            onChange={e => updateForm('destination_url', e.target.value)}
            onBlur={() => setDestinationUrlTouched(true)}
            placeholder="https://example.com"
            className={`${baseCls} ${stateCls}`}
          />
        </div>
        {showDestinationUrlError && (
          <p className="mt-1 text-[11px] text-red-500 dark:text-red-400">
            {trimmedDestinationUrl
              ? 'Enter a valid URL (must start with http:// or https://).'
              : 'Destination URL is required.'}
          </p>
        )}
      </div>
    )
  }

  // ─── Basic Tab ───
  function renderBasicTab() {
    // When the ad is backed by a variant attribute (the AI-generated
    // template flow), the variant's `data` placeholders + custom_image
    // slots are the only fields users should be touching. The classic
    // ad fields (title, description, action text, button colour,
    // content type, overlap type, hero image, has_*/show_*/image_only
    // toggles) come from the template definition and are not meaningful
    // to edit per-variant — we hide them entirely. Status and
    // Destination URL stay visible since both still apply.
    const hasVariantAttribute = showVariantSection
    return (
      <div className="space-y-5">
        {!hasVariantAttribute && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <FieldGroup label="Title">
                <input type="text" value={form.title} onChange={e => updateForm('title', e.target.value)} placeholder="Ad title" className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" />
              </FieldGroup>
              <FieldGroup label="Status">
                {renderStatusSelect()}
              </FieldGroup>
            </div>

            <FieldGroup label="Description">
              <textarea value={form.description} onChange={e => updateForm('description', e.target.value)} placeholder="Ad description" rows={3} className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
            </FieldGroup>

            <div className="grid grid-cols-2 gap-4">
              {renderDestinationUrlField()}
              <FieldGroup label="Action Text (CTA)">
                <input type="text" value={form.action_text} onChange={e => updateForm('action_text', e.target.value)} placeholder="Shop Now" className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </FieldGroup>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FieldGroup label="Button Color">
                <div className="flex items-center gap-2">
                  <input type="color" value={form.button_color} onChange={e => updateForm('button_color', e.target.value)} className="h-8 w-8 rounded border border-gray-200 dark:border-gray-700 cursor-pointer" />
                  <input type="text" value={form.button_color} onChange={e => updateForm('button_color', e.target.value)} className="flex-1 px-2 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 font-mono" />
                </div>
              </FieldGroup>
              <FieldGroup label="Content Type">
                <input type="text" value={form.content_type} onChange={e => updateForm('content_type', e.target.value)} placeholder="standard_template" className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </FieldGroup>
              <FieldGroup label="Overlap Type">
                <select value={form.overlap_type} onChange={e => updateForm('overlap_type', e.target.value)} className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500">
                  {OVERLAP_OPTIONS.map(o => (<option key={o} value={o}>{o.charAt(0).toUpperCase() + o.slice(1)}</option>))}
                </select>
              </FieldGroup>
            </div>

            {/* Image upload */}
            <FieldGroup label="Image">
              <div className="flex items-start gap-3">
                {form.image_url ? (
                  <div className="relative group">
                    <img src={form.image_url} alt="Ad image" className="w-24 h-24 rounded-lg object-cover border border-gray-200 dark:border-gray-700" onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
                    <button onClick={() => updateForm('image_url', '')} className="absolute -top-1.5 -right-1.5 p-0.5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <div onClick={() => imageInputRef.current?.click()} className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center cursor-pointer hover:border-primary-400 dark:hover:border-primary-600 transition-colors">
                    <Upload className="h-5 w-5 text-gray-400 mb-1" />
                    <span className="text-[10px] text-gray-400">Upload</span>
                  </div>
                )}
                <div className="flex-1">
                  <input type="url" value={form.image_url} onChange={e => updateForm('image_url', e.target.value)} placeholder="Or paste image URL..." className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500" />
                  <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                  <p className="text-[10px] text-gray-400 mt-1">Upload or paste an image URL</p>
                </div>
              </div>
            </FieldGroup>

            {/* Toggles */}
            <div className="grid grid-cols-4 gap-3">
              <ToggleField label="Has Action" checked={form.has_action} onChange={v => updateForm('has_action', v)} />
              <ToggleField label="Has Description" checked={form.has_description} onChange={v => updateForm('has_description', v)} />
              <ToggleField label="Show Title" checked={form.show_title} onChange={v => updateForm('show_title', v)} />
              <ToggleField label="Image Only" checked={form.image_only} onChange={v => updateForm('image_only', v)} />
            </div>
          </>
        )}

        {/* Compact Status + Destination URL row when the variant section
            takes over — these are the only two classic ad fields we keep
            visible for variant-backed ads. */}
        {hasVariantAttribute && (
          <div className="grid grid-cols-2 gap-4">
            <FieldGroup label="Status">
              {renderStatusSelect()}
            </FieldGroup>
            {renderDestinationUrlField()}
          </div>
        )}

        {/* ── Variant Attribute ──
            The shape of this section is driven entirely by what the ad
            API returns: every `custom_image_N_url` discovered becomes an
            "Image N" sub-tab, and every key in the variant's `data`
            object becomes an inline text field. Nothing is removable
            from the UI — users only edit existing placeholders. */}
        <CollapsibleSection title="Variant Attribute" icon={<ImageIcon className="h-3.5 w-3.5" />} isOpen={showVariantSection} onToggle={() => setShowVariantSection(v => !v)}>
          <div className="space-y-4">
            {/* Name — used as the variant's display name elsewhere in the
                creative studio, so we keep it as a prominent first field. */}
            <FieldGroup label="Variant Name" compact>
              <input
                type="text"
                value={variant.name}
                onChange={e => updateVariant('name', e.target.value)}
                placeholder="Variant name"
                className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </FieldGroup>

            {/* Images sub-tabs */}
            {variant.images.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                    <ImageIcon className="h-3 w-3" />
                    Custom Images
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {variant.images.length} slot{variant.images.length === 1 ? '' : 's'}
                  </span>
                </div>
                {/* Horizontal sub-tab strip. Scrolls horizontally when the
                    ad has many slots (the screenshot had up to 7). */}
                <div className="flex items-center gap-1 overflow-x-auto pb-1 mb-3 -mx-1 px-1">
                  {variant.images.map((slot, idx) => {
                    const isActive = idx === activeImageSlot
                    // Show the backend key (humanised) as the tab label so
                    // it's obvious which placeholder a user is editing.
                    // The label is precomputed at load time from the
                    // ad's `custom_image_macro_keys` map (or a
                    // "Custom Image N" fallback when the API didn't
                    // ship a mapping). The raw backend key still shows
                    // up in the title attribute for power-users.
                    const rawKey = `custom_image_${slot.index}_url`
                    const tabLabel = slot.label
                    return (
                      <button
                        key={slot.index}
                        onClick={() => setActiveImageSlot(idx)}
                        title={rawKey}
                        // `flex-shrink-0` is critical: the parent strip
                        // is a horizontally-scrolling flex container, but
                        // without it the buttons will *shrink* to fit
                        // when there are many slots and `whitespace-nowrap`
                        // then makes the text overflow visually past the
                        // border (seen as e.g. "Product Image" inside the
                        // active tab and a stray "1" leaking out the
                        // right edge). Pinning the intrinsic width keeps
                        // each label fully contained and forces the parent
                        // to scroll instead.
                        className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border whitespace-nowrap transition-colors ${
                          isActive
                            ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                            : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        {/* Thumbnail bubble so users can scan slots without
                            clicking through each tab. */}
                        {slot.url ? (
                          <img src={slot.url} alt="" className="h-4 w-4 rounded object-cover" />
                        ) : (
                          <span className={`h-4 w-4 rounded border border-dashed ${isActive ? 'border-primary-400' : 'border-gray-300 dark:border-gray-600'}`} />
                        )}
                        {tabLabel}
                        {slot.pendingFile && (
                          <span className="text-[9px] uppercase tracking-wide text-primary-600 dark:text-primary-400">New</span>
                        )}
                      </button>
                    )
                  })}
                </div>

                {/* Active slot editor */}
                {variant.images[activeImageSlot] && (
                  <ImageSlotEditor
                    slot={variant.images[activeImageSlot]}
                    onPickFile={file => handleVariantSlotFile(activeImageSlot, file)}
                    onChangeUrl={url => updateImageSlot(activeImageSlot, { url, pendingFile: null })}
                    onToggleMultiple={v => updateImageSlot(activeImageSlot, { multiple: v })}
                  />
                )}
              </div>
            )}

            {/* Videos sub-tabs — same shape as the image strip above.
                Each entry maps 1:1 with a `custom_template_videos[]`
                item from the API; the sub-tab strip lets users switch
                between them, and the active slot's editor toggles
                between a file-upload widget (when video_type === 'file')
                and a link input for embeds (youtube/vimeo/…). */}
            {variant.videos.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                    <Video className="h-3 w-3" />
                    Custom Videos
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {variant.videos.length} slot{variant.videos.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="flex items-center gap-1 overflow-x-auto pb-1 mb-3 -mx-1 px-1">
                  {variant.videos.map((slot, idx) => {
                    const isActive = idx === activeVideoSlot
                    const rawKey = `custom_video_${slot.index}`
                    return (
                      <button
                        key={slot.index}
                        onClick={() => setActiveVideoSlot(idx)}
                        title={rawKey}
                        className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border whitespace-nowrap transition-colors ${
                          isActive
                            ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                            : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <Video className={`h-3 w-3 ${isActive ? 'text-primary-500' : 'text-gray-400'}`} />
                        {slot.label}
                        {slot.pendingFile && (
                          <span className="text-[9px] uppercase tracking-wide text-primary-600 dark:text-primary-400">New</span>
                        )}
                      </button>
                    )
                  })}
                </div>

                {variant.videos[activeVideoSlot] && (
                  <VideoSlotEditor
                    slot={variant.videos[activeVideoSlot]}
                    onPickFile={file => handleVariantVideoFile(activeVideoSlot, file)}
                    onChangeType={video_type => updateVideoSlot(activeVideoSlot, {
                      video_type,
                      // Switching away from `file` invalidates any
                      // pending upload — the BE branches on `type` and
                      // would ignore the multipart part anyway, so we
                      // drop it here to keep the UI honest about what
                      // will actually be saved.
                      ...(video_type !== 'file' ? { pendingFile: null } : {}),
                    })}
                    onChangeLink={link => updateVideoSlot(activeVideoSlot, { link })}
                    onToggleAutoplay={autoplay => updateVideoSlot(activeVideoSlot, { autoplay })}
                  />
                )}
              </div>
            )}

            {/* Text placeholders (variant.data.*) */}
            {variant.dataKeys.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">Text Placeholders</span>
                  <span className="text-[10px] text-gray-400">{variant.dataKeys.length} field{variant.dataKeys.length === 1 ? '' : 's'}</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {variant.dataKeys.map(key => {
                    const value = variant.data[key] ?? ''
                    const kind = classifyVariantDataField(key, value)
                    const inputCls = 'w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500'
                    return (
                      <div key={key} className={kind === 'longtext' ? 'md:col-span-2' : ''}>
                        <FieldGroup label={humanise(key)} compact>
                          {kind === 'longtext' && (
                            <textarea
                              value={value}
                              onChange={e => updateVariantDataValue(key, e.target.value)}
                              placeholder={key}
                              rows={3}
                              className={`${inputCls} resize-y`}
                            />
                          )}
                          {kind === 'number' && (
                            <input
                              type="number"
                              value={value}
                              onChange={e => updateVariantDataValue(key, e.target.value)}
                              placeholder={key}
                              // `inputMode="decimal"` lets mobile keyboards
                              // offer a numeric pad without locking the
                              // field to integers (some `_num` fields hold
                              // decimals like rates / percentages).
                              inputMode="decimal"
                              className={inputCls}
                            />
                          )}
                          {kind === 'text' && (
                            <input
                              type="text"
                              value={value}
                              onChange={e => updateVariantDataValue(key, e.target.value)}
                              placeholder={key}
                              className={inputCls}
                            />
                          )}
                          {kind === 'color' && (
                            <div className="flex items-center gap-2">
                              <input
                                type="color"
                                // The picker requires a #rrggbb value; the
                                // sibling text input owns the source of
                                // truth so users can paste arbitrary CSS
                                // colours and we don't clobber them.
                                value={toPickerHex(value)}
                                onChange={e => updateVariantDataValue(key, e.target.value)}
                                className="h-8 w-8 rounded border border-gray-200 dark:border-gray-700 cursor-pointer bg-transparent p-0"
                                aria-label={`${humanise(key)} colour picker`}
                              />
                              <input
                                type="text"
                                value={value}
                                onChange={e => updateVariantDataValue(key, e.target.value)}
                                placeholder="#000000"
                                className={`${inputCls} font-mono`}
                              />
                            </div>
                          )}
                          <span className="block mt-0.5 text-[9px] font-mono text-gray-400">{key}</span>
                        </FieldGroup>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {variant.images.length === 0 && variant.videos.length === 0 && variant.dataKeys.length === 0 && (
              <p className="text-[11px] text-gray-400 italic">
                This variant has no image, video, or text placeholders.
              </p>
            )}
          </div>
        </CollapsibleSection>

        {/* The classic Video collapsible (Video Link / Video Type /
            Video Title / Category / Autoplay for `ad.video`) used to
            live here. It duplicated the Custom Videos sub-tabs inside
            the Variant Attribute section — videos are now edited
            exclusively there via `custom_template_videos[]`, so this
            block has been removed. */}
      </div>
    )
  }

  // ─── Advanced Tab ───
  function renderAdvancedTab() {
    return (
      <div className="space-y-5">
        {/* ── Macros ──
            Seeded from the ad's `macros` array on the API. The macro
            list is fixed (no add/remove from the UI) and the name is
            shown read-only — users only change the comparison condition
            and the comma-separated value list, matching the reference
            Income editor layout. */}
        <div>
          <div className="mb-2">
            <span className="text-xs font-semibold tracking-wider uppercase text-gray-700 dark:text-gray-300">Macros</span>
          </div>
          {macros.length === 0 ? (
            <p className="text-[11px] text-gray-400 italic">No macros for this variant</p>
          ) : (
            <div className="space-y-4">
              {macros.map((macro, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-3 items-start">
                  <FieldGroup label="Name" compact className="col-span-3">
                    {/* Name is read-only — backend authors the list and
                        we don't let users rename or remove entries. We
                        still use an <input> for the styling so it
                        visually matches the editable columns, with a
                        muted background to signal it's locked. */}
                    <input
                      type="text"
                      value={macro.name}
                      readOnly
                      tabIndex={-1}
                      className="w-full px-2 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/60 text-gray-700 dark:text-gray-300 font-mono cursor-default focus:outline-none"
                    />
                  </FieldGroup>
                  <FieldGroup label="Condition" compact className="col-span-3">
                    <select
                      value={macro.condition}
                      onChange={e => updateMacro(idx, 'condition', e.target.value)}
                      className="w-full px-2 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      {/* If the backend ever returns a condition not in
                          our vocabulary (e.g. an experimental operator),
                          surface it as a passthrough option so the user
                          still sees what's stored. */}
                      {!MACRO_CONDITION_OPTIONS.some(c => c.value === macro.condition) && macro.condition && (
                        <option value={macro.condition}>{macro.condition.replace(/_/g, ' ')}</option>
                      )}
                      {MACRO_CONDITION_OPTIONS.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </FieldGroup>
                  <FieldGroup label="Value (accepts multiple values separated by comma)" compact className="col-span-6">
                    <input
                      type="text"
                      value={macro.value}
                      onChange={e => updateMacro(idx, 'value', e.target.value)}
                      placeholder="Value"
                      className="w-full px-2 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </FieldGroup>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-2">Publish Settings</span>
          <div className="grid grid-cols-2 gap-3">
            <FieldGroup label="Publish Date" compact><input type="date" value={form.publish_date} onChange={e => updateForm('publish_date', e.target.value)} className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500" /></FieldGroup>
            <FieldGroup label="Publish Time" compact><input type="time" value={form.publish_time} onChange={e => updateForm('publish_time', e.target.value)} className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500" /></FieldGroup>
            <FieldGroup label="Unpublish Date" compact><input type="date" value={form.unpublished_date} onChange={e => updateForm('unpublished_date', e.target.value)} className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500" /></FieldGroup>
            <FieldGroup label="Unpublish Time" compact><input type="time" value={form.unpublished_time} onChange={e => updateForm('unpublished_time', e.target.value)} className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500" /></FieldGroup>
          </div>
        </div>

        {/* "Additional Settings" (Facebook Headline / OG Description /
            Short URL / Label Title + colours / Include Like-Share /
            Default Ad) used to live here. They were holdovers from the
            legacy ad editor and aren't meaningful for variant-driven
            templates, so the whole block has been dropped from the
            Advanced tab. The underlying state (form.headline_fb,
            form.og_description, …) is still wired into the API payload
            in handleSave so existing values round-trip untouched; we
            just no longer expose them in the UI. */}
      </div>
    )
  }

  // ─── Trackers Tab ───
  function renderTrackersTab() {
    return (
      <div className="space-y-5">
        <FieldGroup label="Impression Tracker">
          <input type="url" value={form.impression_tracker} onChange={e => updateForm('impression_tracker', e.target.value)} placeholder="https://tracker.com/impression" className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500" />
        </FieldGroup>

        <div>
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 block mb-2">Video Trackers</span>
          <div className="space-y-2">
            {([['video_5_tracker', '5% Tracker'], ['video_25_tracker', '25% Tracker'], ['video_50_tracker', '50% Tracker'], ['video_75_tracker', '75% Tracker'], ['video_100_tracker', '100% Tracker']] as const).map(([key, label]) => (
              <FieldGroup key={key} label={label} compact>
                <input type="url" value={form[key]} onChange={e => updateForm(key, e.target.value)} placeholder={`https://tracker.com/${key}`} className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </FieldGroup>
            ))}
          </div>
        </div>

        {/* ── Custom Trackers ──
            Flat per-row layout matching the Income reference editor:
            Placement Name | DV360 LineItem ID | Size dropdown |
            Imp. Tracker | Click Tracker | trash. The size dropdown is
            sourced from the parent ad-unit's `sizes` array so users
            can't pick a size the unit doesn't actually serve. */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">Custom Trackers</span>
            <button onClick={addTracker} className="flex items-center gap-1 text-[11px] text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"><Plus className="h-3 w-3" /> Add Tracker</button>
          </div>
          {trackers.length === 0 ? (
            <p className="text-[11px] text-gray-400 italic">No custom trackers</p>
          ) : (
            <div className="space-y-3">
              {trackers.map((tracker, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                  <FieldGroup label="Placement Name" compact className="col-span-2">
                    <input
                      type="text"
                      value={tracker.name}
                      onChange={e => updateTracker(idx, 'name', e.target.value)}
                      placeholder="Placement Name"
                      className="w-full px-2 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </FieldGroup>
                  <FieldGroup label="DV360 LineItem ID" compact className="col-span-2">
                    <input
                      type="text"
                      // `macro_value` is what backends use to scope a
                      // tracker to a specific DV360 line item — Income's
                      // editor labels it as such in the UI.
                      value={tracker.macro_value || ''}
                      onChange={e => updateTracker(idx, 'macro_value', e.target.value || null)}
                      placeholder="DV360 LineItem ID"
                      className="w-full px-2 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </FieldGroup>
                  <FieldGroup label="Size" compact className="col-span-2">
                    <select
                      value={tracker.ad_unit_size || ''}
                      onChange={e => updateTracker(idx, 'ad_unit_size', e.target.value || null)}
                      className="w-full px-2 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      {/* Empty value = "applies to every size on this
                          ad unit" (`ad_unit_size: null` on save). */}
                      <option value="">All Sizes</option>
                      {/* If the backend stored a size we don't recognise
                          (e.g. the ad unit was reshaped), surface it as
                          a passthrough so the user sees the actual
                          stored value and can fix it. */}
                      {tracker.ad_unit_size && !adUnitSizes.includes(tracker.ad_unit_size) && (
                        <option value={tracker.ad_unit_size}>{tracker.ad_unit_size}</option>
                      )}
                      {adUnitSizes.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </FieldGroup>
                  <FieldGroup label="Imp. Tracker" compact className="col-span-2">
                    <input
                      type="url"
                      value={tracker.impression_url || ''}
                      onChange={e => updateTracker(idx, 'impression_url', e.target.value || null)}
                      placeholder="Imp. Tracker"
                      className="w-full px-2 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </FieldGroup>
                  <FieldGroup label="Click Tracker" compact className="col-span-3">
                    <input
                      type="url"
                      value={tracker.click_url || ''}
                      onChange={e => updateTracker(idx, 'click_url', e.target.value || null)}
                      // Click trackers are pixel/redirect URLs that fail
                      // silently when served bare (e.g. `tracker.com/x`),
                      // so we auto-prefix `http://` on blur if the user
                      // didn't include one. Only `http(s)://` schemes
                      // are recognised; we don't touch other schemes
                      // (`mailto:` etc.) since those are intentional.
                      onBlur={e => {
                        const v = e.target.value.trim()
                        if (!v) return
                        if (/^https?:\/\//i.test(v)) return
                        updateTracker(idx, 'click_url', `http://${v}`)
                      }}
                      placeholder="http://tracker.com/click"
                      className="w-full px-2 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </FieldGroup>
                  <div className="col-span-1 flex items-center justify-end pb-1.5">
                    <button
                      onClick={() => removeTracker(idx)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Remove tracker"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => { if (typeof reader.result === 'string') updateForm('image_url', reader.result) }
    reader.readAsDataURL(file)
    e.target.value = ''
  }

}

// ─── Sub-components ───

function FieldGroup({ label, compact, className, children }: { label: string; compact?: boolean; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <label className={`block font-medium text-gray-600 dark:text-gray-400 mb-1 ${compact ? 'text-[10px]' : 'text-[11px]'}`}>{label}</label>
      {children}
    </div>
  )
}

function ToggleField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (val: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} className={`relative inline-flex h-4 w-7 items-center rounded-full transition-colors ${checked ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-3.5' : 'translate-x-0.5'}`} />
      </button>
      <span className="text-[11px] text-gray-700 dark:text-gray-300">{label}</span>
    </label>
  )
}

/**
 * Editor for a single `custom_image_N` slot inside the variant attribute
 * section. Shows the current image (or an upload dropzone if blank), an
 * editable URL field, a file picker for replacement, and the
 * `_multiple` toggle. The slot is never destroyed by the UI — clearing
 * the URL only clears the *value*, the backend placeholder stays.
 */
function ImageSlotEditor({
  slot,
  onPickFile,
  onChangeUrl,
  onToggleMultiple,
}: {
  slot: CustomImageSlot
  onPickFile: (file: File) => void
  onChangeUrl: (url: string) => void
  onToggleMultiple: (v: boolean) => void
}) {
  const fileInputRef = useRef<HTMLInputElement>(null!)
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40 p-3 space-y-3">
      <div className="flex items-start gap-3">
        {slot.url ? (
          <div className="relative group flex-shrink-0">
            <img
              src={slot.url}
              alt={`Custom image ${slot.index}`}
              className="w-24 h-24 rounded-lg object-cover border border-gray-200 dark:border-gray-700 bg-white"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="absolute inset-0 rounded-lg bg-black/50 text-white text-[10px] font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1"
            >
              <Upload className="h-3 w-3" /> Replace
            </button>
          </div>
        ) : (
          <div
            onClick={() => fileInputRef.current?.click()}
            className="w-24 h-24 flex-shrink-0 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center cursor-pointer hover:border-primary-400 dark:hover:border-primary-600 transition-colors"
          >
            <Upload className="h-4 w-4 text-gray-400 mb-1" />
            <span className="text-[10px] text-gray-400">Upload</span>
          </div>
        )}
        <div className="flex-1 min-w-0 space-y-2">
          <FieldGroup label="Image URL" compact>
            <input
              type="url"
              value={slot.pendingFile ? '' : slot.url}
              onChange={e => onChangeUrl(e.target.value)}
              placeholder={slot.pendingFile ? 'Pending upload — saved on submit' : 'https://…'}
              disabled={!!slot.pendingFile}
              className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-60"
            />
          </FieldGroup>
          <div className="flex items-center justify-between">
            <ToggleField label="Multiple Images" checked={slot.multiple} onChange={onToggleMultiple} />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-[11px] text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors inline-flex items-center gap-1"
            >
              <Upload className="h-3 w-3" /> {slot.url ? 'Replace file' : 'Upload file'}
            </button>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0]
            if (file) onPickFile(file)
            e.target.value = ''
          }}
        />
      </div>
      <div className="flex items-center justify-between gap-2 text-[10px] text-gray-400">
        <span className="font-mono">custom_image_{slot.index}_url</span>
        <span>Max {MAX_VARIANT_IMAGE_KB} KB</span>
      </div>
    </div>
  )
}

/**
 * Editor for a single `custom_template_videos[]` entry inside the variant
 * attribute section. Symmetric with {@link ImageSlotEditor} but adapts
 * its body based on `slot.video_type`:
 *
 *   - `'file'`  → file upload widget (preview the picked clip via
 *                 `<video>`, accept-only video MIME) + size-cap hint.
 *   - other     → a single URL/link input with a type-aware placeholder
 *                 (YouTube / Vimeo / custom embed).
 *
 * The video-type dropdown is always visible at the top of the slot so
 * users can switch modes; the autoplay toggle lives at the bottom and
 * applies to both modes (rendered embeds also respect autoplay).
 */
function VideoSlotEditor({
  slot,
  onPickFile,
  onChangeType,
  onChangeLink,
  onToggleAutoplay,
}: {
  slot: CustomVideoSlot
  onPickFile: (file: File) => void
  onChangeType: (videoType: string) => void
  onChangeLink: (link: string) => void
  onToggleAutoplay: (v: boolean) => void
}) {
  const fileInputRef = useRef<HTMLInputElement>(null!)
  const isFileMode = slot.video_type === 'file'
  // Placeholder URL hint keyed on the active video type so users know
  // what shape of link to paste. Falls back to a generic embed URL
  // hint for any future / unknown type.
  const linkPlaceholder = (() => {
    switch (slot.video_type) {
      case 'youtube': return 'https://www.youtube.com/watch?v=…'
      case 'vimeo':   return 'https://vimeo.com/…'
      default:        return 'https://…'
    }
  })()
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40 p-3 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <FieldGroup label="Video Type" compact>
          <select
            value={slot.video_type}
            onChange={e => onChangeType(e.target.value)}
            className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {/* If the BE shipped a video_type the variant editor
                doesn't expose (e.g. legacy `custom`, or the storage
                twin `uploaded` that we normalise to `file` for editing),
                surface it as a passthrough option so the user still
                sees what's actually stored — but they can switch to
                one of the three first-class options at any time. */}
            {!VARIANT_VIDEO_TYPE_OPTIONS.some(o => o.value === slot.video_type) && slot.video_type && (
              <option value={slot.video_type}>{slot.video_type}</option>
            )}
            {VARIANT_VIDEO_TYPE_OPTIONS.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </FieldGroup>
        <div className="flex items-end">
          <ToggleField label="Autoplay" checked={slot.autoplay} onChange={onToggleAutoplay} />
        </div>
      </div>

      {isFileMode ? (
        <div className="flex items-start gap-3">
          {slot.url ? (
            <div className="relative group flex-shrink-0">
              {/* Use a real <video> for the preview so users can scrub
                  / verify the upload. `muted` keeps the browser from
                  blocking autoplay if it kicks in on hover. */}
              <video
                src={slot.url}
                className="w-32 h-20 rounded-lg object-cover border border-gray-200 dark:border-gray-700 bg-black"
                muted
                preload="metadata"
                playsInline
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 rounded-lg bg-black/50 text-white text-[10px] font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1"
              >
                <Upload className="h-3 w-3" /> Replace
              </button>
            </div>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="w-32 h-20 flex-shrink-0 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center cursor-pointer hover:border-primary-400 dark:hover:border-primary-600 transition-colors"
            >
              <Upload className="h-4 w-4 text-gray-400 mb-1" />
              <span className="text-[10px] text-gray-400">Upload video</span>
            </div>
          )}
          <div className="flex-1 min-w-0 space-y-2">
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Upload an MP4/WebM clip. The video is uploaded on save and the
              backend hosts it for you.
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-[11px] text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors inline-flex items-center gap-1"
            >
              <Upload className="h-3 w-3" /> {slot.url ? 'Replace file' : 'Upload file'}
            </button>
            {slot.pendingFile && (
              <p className="text-[10px] text-gray-400">
                Pending: {slot.pendingFile.name} ({(slot.pendingFile.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0]
              if (file) onPickFile(file)
              e.target.value = ''
            }}
          />
        </div>
      ) : (
        <FieldGroup label="Video Link" compact>
          <input
            type="url"
            value={slot.link}
            onChange={e => onChangeLink(e.target.value)}
            placeholder={linkPlaceholder}
            className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </FieldGroup>
      )}

      <div className="flex items-center justify-between gap-2 text-[10px] text-gray-400">
        <span className="font-mono">custom_video_{slot.index}_{isFileMode ? 'url' : 'link'}</span>
        {isFileMode && <span>Max {MAX_VARIANT_VIDEO_MB} MB</span>}
      </div>
    </div>
  )
}

function CollapsibleSection({ title, icon, isOpen, onToggle, children }: { title: string; icon: React.ReactNode; isOpen: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800/80 transition-colors">
        <div className="flex items-center gap-2">
          <span className="text-gray-500 dark:text-gray-400">{icon}</span>
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{title}</span>
        </div>
        {isOpen ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
      </button>
      {isOpen && <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-800">{children}</div>}
    </div>
  )
}
